import { useState } from "react";
import {
  Card, CardContent, Typography, FormControlLabel, Divider,
  Box, CardMedia, Grid, Button, Switch, Skeleton, Chip, Tooltip
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import PropTypes from "prop-types";
import moment from "moment";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import { updateProfileServiceStatus } from "../../actions/profile";
import { isEmpty, urlImg, ColorButton } from "../common/helpers";
import { esperaFacilitador } from "../common/imparte";
import { dinero } from "../../utils/dinero";

import { useDispatch } from "react-redux";

const ServiceEditCard = ({
  type, servicio, borrador = false, irA,
  eliminar
}) => {
  const dispatch = useDispatch();
  const params = new URLSearchParams();
  params.append('service', servicio?._id);

  // URL base de la imagen en Cloudinary
  const getBaseImgUrl = () =>
    !isEmpty(servicio?.img) && Array.isArray(servicio.img) && servicio.img.length > 0
      ? `${urlImg}/servicios/${servicio._id}/${servicio.img[0].nombre}`
      : null;

  const [imgSrc, setImgSrc] = useState(getBaseImgUrl() || '/search.png');
  const [retries, setRetries] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const MAX_RETRIES = 4;

  // Sin useEffect: imgLoaded solo cambia vía onLoad/onError.
  // El useEffect con servicio.img como dependencia reseteaba imgLoaded en cada
  // re-render del padre (servicio.img es array → nueva referencia siempre),
  // dejando el skeleton pegado porque onLoad no se dispara si el src no cambió.

  // Si Cloudinary aún no tiene la imagen disponible, reintenta con backoff (1s, 2s, 3s, 4s)
  const handleImgError = () => {
    const base = getBaseImgUrl();
    if (!base || retries >= MAX_RETRIES) {
      setImgSrc('/search.png');
      setImgLoaded(true);
      return;
    }
    const nextRetry = retries + 1;
    setTimeout(() => {
      setImgSrc(`${base}?r=${nextRetry}`);
      setRetries(nextRetry);
    }, nextRetry * 1000);
  };

  const cambiarStatus = async status => {
    await dispatch(updateProfileServiceStatus({ status }, servicio._id));
  }

  // Si el servicio está en un grupo o negocio con otra persona para impartirlo (lo manda el servidor en `facilitador`):
  // mientras esa persona no acepta, o si rechazó, el público no lo ve ni puede contratarlo. Se avisa aquí y se lleva a la página.
  const facilitador = !borrador && servicio?.facilitador?.estado ? servicio.facilitador : null;
  const quien = facilitador?.nombre || 'la persona elegida';
  const avisoFacilitador = !facilitador ? null
    : facilitador.estado === 'pendiente' ? {
      etiqueta: `Esperando a ${quien}`,
      ayuda: `No se ve ni se puede contratar hasta que ${quien} acepte impartirlo en «${facilitador.pagina}». Toca para ver la página.`,
      color: 'warning.main',
    }
      : facilitador.estado === 'rechazado' ? {
        etiqueta: `${quien} rechazó impartirlo`,
        ayuda: `No se ve ni se puede contratar. Entra a «${facilitador.pagina}» para elegir a otra persona o impartirlo tú.`,
        color: 'error.main',
      }
        : {
          etiqueta: `Lo imparte ${quien}`,
          ayuda: `En tu página «${facilitador.pagina}». Las reservas y los mensajes te siguen llegando a ti.`,
          color: 'sutil',
        };

  return (
    <Card className="card-edit" sx={{ height: 'auto', minHeight: { md: 200 }, flexDirection: { xs: 'column', sm: 'row' } }}>
      <Box sx={{ width: { xs: '100%', sm: 200 }, height: { xs: 160, sm: 'auto' }, minHeight: 160, flexShrink: 0, alignSelf: 'stretch', position: 'relative' }}>
        <CardMedia
          component="img"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', visibility: imgLoaded ? 'visible' : 'hidden' }}
          image={imgSrc}
          alt={servicio?.nombre}
          onLoad={() => setImgLoaded(true)}
          onError={handleImgError}
        />
        {!imgLoaded && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          />
        )}
        {servicio?.bloqueadoCedula && !borrador && (
          <Tooltip title="Este servicio no será visible ni contratable hasta que tu cédula profesional sea aprobada.">
            <Chip
              icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 15 }} />}
              label="Pendiente de cédula"
              size="small"
              sx={{
                position: 'absolute', top: 8, left: 8, bgcolor: (t) => alpha(t.palette.warning.main, 0.3), color: 'warning.oscuro',
                fontWeight: 700, height: 24, '& .MuiChip-icon': { color: 'warning.oscuro' }
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box className="container-body" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            {
              !isEmpty(servicio?.nombre)
                ? servicio?.nombre
                : ('Sin nombre')
            }
          </Typography>
          {avisoFacilitador && (
            <Box sx={{ my: 0.5 }}>
              <Tooltip title={avisoFacilitador.ayuda}>
                <Chip
                  size="small"
                  clickable
                  onClick={() => irA(`/user/crear-servicio/1?edit=${facilitador.paginaId}&type=group`)}
                  icon={esperaFacilitador(facilitador.estado)
                    ? <HourglassEmptyIcon sx={{ fontSize: 15, color: 'primary.main !important' }} />
                    : <PersonOutlineIcon sx={{ fontSize: 15, color: 'primary.main !important' }} />}
                  label={avisoFacilitador.etiqueta}
                  sx={{ bgcolor: avisoFacilitador.color, color: 'primary.main', fontWeight: 700, maxWidth: '100%' }}
                />
              </Tooltip>
            </Box>
          )}
          <Grid container>
            <Grid size={{ xs: 12, md: 9 }}>
              <Typography variant="body2" color="text.secondary" sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}>
                {!isEmpty(servicio?.descripcion) ? servicio.descripcion : 'Descripción pendiente'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} sx={{ textAlign: "end" }}>
              <Typography
                variant="h6"
                color="primary"
                fontWeight="bold"
                sx={{ mt: 1 }}
              >
                {
                  !isEmpty(servicio?.costoServicio) ? dinero(servicio.costoServicio) : 'Pendiente'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {
                  !isEmpty(servicio?.tipoCosto) ? servicio.tipoCosto : "costo por servicio"
                }
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: "flex-start",
              alignItems: "center",
              paddingBottom: 1,
              minHeight: 28,
            }}
          >
            {!isEmpty(servicio?.ubicacion?.direccion) && (
              <Typography sx={{ marginRight: "0.75rem!important", minWidth: 0, maxWidth: '100%' }} className="text-icon" variant="body2" color="primary">
                <LocationOnIcon sx={{ fontSize: 20, marginRight: 1, flexShrink: 0 }} />
                {/* Dirección en una sola línea con elipsis: antes envolvía en 2-3 líneas y se veía apretado */}
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {servicio?.ubicacion?.direccion}
                </Box>
              </Typography>
            )}
          </Grid>
          <Divider />
          <Grid container sx={{ mt: 'auto', pt: 1 }}>
            <Grid
              size={{ xs: 12, md: 8 }}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Typography variant="body2" color="text.secondary">
                Creado el {moment(servicio?.date).format("DD MMMM YYYY")}
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 12, md: 4 }}
              mt={{ xs: 2, md: 0 }}
              className="card-actions"
            >
              {
                (type.includes("SERVICE") && !borrador) && (
                  <Box className="toggle">
                    <Typography variant="body2" color="text.secondary" sx={{ marginRight: 1 }}>
                      {
                        servicio?.status ? "Activo" : "Inactivo"
                      }
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch checked={servicio?.status} onClick={() => cambiarStatus(!servicio?.status)} name="status" />
                      }
                      label=""
                    />
                  </Box>
                )
              }
              {
                !borrador && (
                  <Button
                    color="success"
                    variant="contained"
                    size="small"
                    endIcon={<InsertLinkIcon />}
                    sx={{ color: "white", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
                    onClick={() => irA(`/user/servicio/${servicio._id}`)}
                  >
                    Ver
                  </Button>
                )
              }
              <ColorButton
                variant="contained"
                size="small"
                endIcon={<DriveFileRenameOutlineIcon />}
                sx={{ color: "white", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
                onClick={() => irA(`/user/crear-servicio/${type === "SERVICE" ? 0 : 1}?${params.toString()}`)}
              >
                Editar
              </ColorButton>
              <Button
                color="error"
                variant="contained"
                size="small"
                endIcon={<DeleteIcon />}
                sx={{ color: "white", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
                onClick={() => eliminar(servicio?._id)}
              >
                Eliminar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Box>
    </Card>
  );
};

ServiceEditCard.propTypes = {
  eliminar: PropTypes.func.isRequired,
  irA: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  servicio: PropTypes.object.isRequired,
  borrador: PropTypes.bool,
}

export default ServiceEditCard;
